import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CustomerService from '../services/customerService';
import { Customer } from '../types';
import AuthService from '../services/authService';
// CSS đã được hợp nhất vào App.css

const CustomerList: React.FC = () => {
    const navigate = useNavigate();
    const currentUser = AuthService.getCurrentUser();
    const userPrefix = currentUser?.role === 'staff' ? '/staff' : '/admin';

    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

    // Debounce search function
    const debouncedSearch = useCallback(() => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeout = setTimeout(() => {
            searchCustomers();
        }, 500); // Chờ 500ms sau khi người dùng ngừng gõ

        setSearchTimeout(timeout);
    }, [searchTerm]);

    // Load customers on mount and when search term changes
    useEffect(() => {
        // Nếu không có từ khóa tìm kiếm, load tất cả khách hàng
        if (!searchTerm.trim()) {
            loadCustomers();
        } else {
            // Nếu có từ khóa tìm kiếm, sử dụng debounced search
            debouncedSearch();
        }
        
        // Cleanup function
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTerm, debouncedSearch]);

    const loadCustomers = async () => {
        try {
            setLoading(true);
            const response = await CustomerService.getAll();
            setCustomers(response.data);
            setError(null);
        } catch (err) {
            setError('Không thể tải danh sách khách hàng. Vui lòng thử lại sau.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const searchCustomers = async () => {
        try {
            setLoading(true);
            // Tìm kiếm theo cả số điện thoại và tên
            const searchParams: { phoneNumber?: string; name?: string } = {
                phoneNumber: searchTerm.trim(),
                name: searchTerm.trim()
            };

            const response = await CustomerService.search(searchParams);
            setCustomers(Array.isArray(response.data) ? response.data : [response.data]);
            setError(null);
        } catch (err) {
            setError('Không thể tìm kiếm khách hàng. Vui lòng thử lại sau.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('Bạn có chắc chắn muốn xóa khách hàng này?')) {
            try {
                await CustomerService.remove(id);
                loadCustomers();
            } catch (err) {
                setError('Không thể xóa khách hàng. Vui lòng thử lại sau.');
                console.error(err);
            }
        }
    };

    const handleRowClick = (customerId: number) => {
        navigate(`${userPrefix}/customers/${customerId}/orders`);
    };

    const handleEditClick = (e: React.MouseEvent, customerId: number) => {
        e.stopPropagation(); // Ngăn chặn event bubbling để không trigger handleRowClick
        navigate(`${userPrefix}/customers/${customerId}`);
    };

    return (
        <div className="content CustomCSS-Container">
            <div className="row">
                <div className="col-md-12">


                    <div className="CustomCSS-SearchRow">

                        <div className="CustomCSS-SearchInputContainer">
                            <input
                                type="text"
                                className="form-control CustomCSS-SearchInput"
                                placeholder="Tìm theo SĐT hoặc tên"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="CustomCSS-AddButtonContainer">
                            <Link to={`${userPrefix}/customers/add`} className="btn btn-success CustomCSS-AddButton" style={{ fontSize: '1rem' }}>
                                <i className="fa-solid fa-plus nc-simple-add mr-2"></i> Thêm
                            </Link>
                        </div>
                    </div>

                    {error && <div className="alert alert-danger CustomCSS-ErrorAlert">{error}</div>}

                    {loading ? (
                        <div className="center-screen">
                            <div className="spinner-border text-primary CustomCSS-Spinner" role="status" aria-label="loading"></div>
                        </div>
                    ) : (
                        <div className="CustomCSS-TableContainer">
                            <table className="table table-hover CustomCSS-Table">
                                <thead className="text-primary CustomCSS-TableHeader">
                                    <tr>
                                        <th className="d-none d-md-table-cell">ID</th>
                                        <th>SĐT</th>
                                        <th className="d-table-cell">Tên</th>
                                        <th className="d-none d-md-table-cell">Địa chỉ</th>
                                        <th className="d-none d-md-table-cell">Ngày tạo</th>
                                        <th>Sửa</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customers.length > 0 ? (
                                        customers.map((customer) => (
                                            <tr 
                                                key={customer.id} 
                                                className="CustomCSS-TableRow" 
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => customer.id && handleRowClick(customer.id)}
                                            >
                                                <td className="d-none d-md-table-cell">{customer.id}</td>
                                                <td>{customer.phoneNumber}</td>
                                                <td className="d-table-cell">{customer.name || 'N/A'}</td>
                                                <td className="d-none d-md-table-cell">{customer.address || 'N/A'}</td>
                                                <td className="d-none d-md-table-cell">
                                                    {customer.createdAt
                                                        ? new Date(customer.createdAt).toLocaleDateString()
                                                        : 'N/A'}
                                                </td>
                                                <td className="table-actions" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={(e) => customer.id && handleEditClick(e, customer.id)}
                                                        className="btn btn-warning me-1 CustomCSS-ActionButton CustomCSS-EditButton"
                                                        aria-label="Sửa khách hàng"
                                                        title="Sửa khách hàng"
                                                    >
                                                        <i className="fa-solid fa-pen"></i>
                                                    </button>
                                                    {/* 
                               
                              <Link
                                to={`${userPrefix}/customers/edit/${customer.id}`}
                                className="btn btn-warning btn-sm me-1 CustomCSS-ActionButton CustomCSS-EditButton"
                              >
                                <i className="nc-icon nc-ruler-pencil"></i> Sửa
                              </Link>
                              <button
                                className="btn btn-danger btn-sm CustomCSS-ActionButton CustomCSS-DeleteButton"
                                onClick={() => customer.id && handleDelete(customer.id)}
                              >
                                <i className="nc-icon nc-simple-remove"></i> Xóa
                              </button>
                               */ }
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={6} className="text-center CustomCSS-EmptyState">
                                                Không tìm thấy khách hàng nào
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CustomerList; 